<template lang="pug">
div
	.infosheet
		v-card.mb-6(outlined)
			factsheet(v-bind="{minElevation:area.minElevation, maxElevation:area.maxElevation, slopeinfo:area.slopeinfo, clusters:area.clusters, snowheights: area.snowheights, skimap:area.skimap, name:area.name, slug:area.slug, accommodations:area.stats.accommodationCount, skischools:area.stats.lessonCount, rentals:area.stats.rentalCount}" )
		ad(location="sidebar").mb-6
		v-card.mt-6.mb-6(outlined color="info" v-if="area.features.length")
			v-card-text.text--primary
				features(:features="area.features")
		snow-alarm.mb-6(:initial-destination-id="area.id" initial-destination-type="area" :initial-destination-name="area.name")
	div
		title-with-reviews(:title="area.name" :reviews="area.reviews" :reviewsLink="localePath({name:'areas-area-reviews',params: {area: area.slug}})")

		//- Travel
		.flow-left
			travelbox.mt-3.mb-6(:destination-slug="area.slug" destination-type="area")

		//- Video
		div.mb-8.flow-left(style="position:relative;" v-if="area.defaultVideo && area.defaultVideo.videoId")
			nuxt-link(:to="localePath({name: 'areas-area-video', params: {area: area.slug}})" :title="$t('page.areas._area:videos.show-more-videos.title', {name:area.name})")
				.cta-video.mb-5 {{$t('area-video:watch.our.review.in.three.minutes', {area: area.name})}}
			v-responsive( :aspect-ratio="16/9")
				vue-plyr(v-if="area.defaultVideo.videoSource === 'youtube' || area.defaultVideo.videoSource === 'vimeo'" ref="plyr" :options="{controls:['play', 'progress', 'volume', 'play-large', 'airplay', 'fullscreen', 'pip', 'mute'], disableContextMenu: false}" :key="area.defaultVideo.videoId")
					div(:data-plyr-provider="area.defaultVideo.videoSource" :data-plyr-embed-id="area.defaultVideo.videoId")
				video-hls(v-else-if="area.defaultVideo.videoSource === 'video'" :videoUrl="area.defaultVideo.videoId")

		//- Content
		h2.my-3.subtitle {{contentpage.name}}
		ws-content(v-if="introtext" :value="introtext")
		.flow-left.mb-6
			div(v-if="area.slopeinfo.hasSummerSkiing && (showFullIntro || !introtext.isSummary)")
				h3 {{$t('page.areas._area:summerski.heading')}}
				p
					| {{$t('page.areas._area:summerski')}}
					|
					nuxt-link(:to="localePath({name: 'areas'})") {{$t('page.areas._area:summerski.more')}}
			.text-center(v-if="!showFullIntro && introtext.isSummary")
				v-btn.mt-4(color="primary" outlined @click="showFullIntro = true") {{$t('page.areas._area:read_full_intro')}}

		//- Covid-19
		.flow-left.my-6(v-if="area.corona && area.corona.isActive")
			corona-info(:destination="area")

		//- Photos
		.flow-left.mb-6(v-if="this.area.photos.length")
			h2 {{$t('page.areas._area:photos.heading')}}
			gallery-embed(:photos="this.area.photos" fill)
			v-divider.mt-3

		//- Pistes
		.flow-left(v-if="area.pistes.length")
			h2.mb-6 {{$t('page.areas._area:pistes.heading')}}
			.piste-list.mb-3
				div(v-for="piste in area.pistes" :class="`piste-${piste.color}`")
					h3
						ws-icon(small) angle-right
						| {{piste.name}}
					ws-content.pl-3(:value="piste.content")
			v-divider.my-6

		.flow-left
			weblog-list-embed(:destinations="[{id: area.id}]" display="cards" limit="6" show-divider)

		//- Reachability
		.flow-left(v-if="area.departures.city || area.gateways.length || area.bottlenecks.length")
			h2
				nuxt-link.black--text(:to="localePath({name: 'areas-area-reachability', params: { area: area.slug }})" :title="$t('page.areas._area:reachability.heading_title')") {{$t('page.areas._area:reachability.heading')}}
			v-row(no-gutters v-if="area.departures.city && area.departures.city.distance !== 9999")
				v-col.shrink
					ws-icon(small) angle-right
				v-col
					i18n(path="page.resorts._resort.reachability:by_car.text_{distance}_{city}_{map}_{highway}" )
						template(#highway) {{area.departures.highway.distance}}
						template(#city) {{area.departures.city.node.name}}
						template(#distance) {{area.departures.city.distance}}
						template(#map)
							a(:href="`https://www.google.nl/maps/dir/${area.departures.city.node.name}/${area.geography.location.coordinates[1]},${area.geography.location.coordinates[0]}`" target="_blank" slot="map") {{$t('page.areas._area.reachability:show_route_link')}}

			h3.mt-6(v-if="area.gateways.length > 1") {{$t('page.areas._area.reachability:gateways_title')}}
			v-row(no-gutters v-for="(gateway, index) in area.gateways" :key="gateway.id")
				v-col.shrink
					ws-icon(small) angle-right
				v-col {{gateway.content.markdown.replace('{prefix}', gatewayPrefix(index))}}

			v-row(no-gutters v-if="area.departures.airports.length" v-for="(airport, index) in area.departures.airports" :key="airport.id")
				v-col.shrink
					ws-icon(small) angle-right
				v-col
					i18n(path="page.areas._area:reachability.airport_nearby_{airport}_{distance}_{link}")
						template(#airport) {{airport.node.name}}
						template(#distance) {{airport.distance}}
						template(#link)
							nuxt-link(:to="localePath({name: 'areas-area-reachability', params: { area: area.slug }})") {{$t('page.areas._area:reachability:airports_link_{area}', {area: area.name})}}

			.text-center
				v-btn.mt-4(color="primary" outlined nuxt :to="localePath({name: 'areas-area-reachability', params: { area: area.slug }})") {{$t('page.areas._area:reachability.button.more')}}
			v-divider.my-6


		//- Skipas
		.flow-left(v-if="skipasArray.length")
			nuxt-link(nuxt :to="localePath({name: 'areas-area-skipas', params: { area: area.slug }})")
				h2.mb-0.black--text {{$t('page.areas._area:skipasprices.heading')}}
			skipas-price-table(:skipas="skipasArray" :destination="area.name")
			.text-center
				v-btn.mt-4(color="primary" outlined nuxt :to="localePath({name: 'areas-area-skipas', params: { area: area.slug }})") {{$t('page.areas._area:skipasprices.more')}}
			v-divider.my-6

		//- Reviews
		.flow-left.area-reviews(v-if="area.reviews.nodes.length > 0")
			nuxt-link(nuxt :to="localePath({name: 'areas-area-reviews', params: { area: area.slug }})" :title="$t('page.areas._area:reviews.heading_link_title_{area}', {area: area.name})")
				h2.black--text {{$tc('page.areas._area:reviews.heading_{rating}_{count}', area.reviews.voteCount, {rating: area.reviews.average, total: area.reviews.voteCount})}}
			review-list.user-content--no-border(:items="area.reviews.nodes")
			.text-center
				v-btn(color="primary" outlined nuxt :to="localePath({name: 'areas-area-reviews', params: { area: area.slug }})") {{$t('page.areas._area:reviews.button.more')}}
			v-divider.my-6

		//- Resorts
		.flow-left(v-if="area.stats.resortCount")
			nuxt-link(nuxt :to="localePath({name: 'areas-area-resorts', params: { area: area.slug }})" :title="$t('page.areas._area:resorts.heading_link_title_{area}', {area: area.name})")
				h2.mt-4.black--text {{$t('page.areas._area:resorts.heading')}}
			resort-table(:resorts="area.resorts.nodes" compact)
			.text-center
				v-btn.mt-4(color="primary" outlined nuxt :to="localePath({name: 'areas-area-resorts', params: { area: area.slug }})") {{$t('page.areas._area:resorts.more')}}
			v-divider.my-6

		//- Checklist
		.flow-left(v-if="['nl-NL', 'nl-BE'].includes($i18n.locale)")
			h2 {{$t('page.areas._area:checklist.heading')}}
			ws-checklist(:area-id="area.id").flow-left
			v-divider.my-6

		//- Forum
		.flow-left(v-if="area.topics.topics.length")
			h2.mt-4.black--text {{$t('page.area._area:forum.heading')}}
			topic-list(:topics="area.topics.topics" :destination="area" hide-destination hide-last-visit-bar)
			.text-center(v-if="area.topics.pagination.total>10")
				v-btn(outlined color="primary" nuxt :to="localePath({name: 'areas-area-forum', params: {area: area.slug}})") {{$t('page.forum:button.all.topics', {count:area.topics.pagination.total})}}

			v-divider.my-6

		//- Forecast
		.flow-left(v-if="area.stats.hasForecast")
			nuxt-link(nuxt :to="localePath({name: 'areas-area-forecast', params: { area: area.slug }})" :title="$t('page.area._area:weather.heading_link_title_{area}', {area: area.name})")
				h2.mt-4.black--text {{$t('page.area._area:weather.heading')}}
			forecast-table(:areaslug="area.slug" :calculateInfosheetWidth="$i18n.locale === 'de-DE'" :initial-active-resort-id="area.mainResort ? area.mainResort.id : false")
			v-divider.my-6

		//- Roel
		template(v-if="area.stats.hasForecast && ['nl-NL', 'nl-BE'].includes($i18n.locale) && weblog")
			weather-report(:weblog="weblog")
			v-divider.my-6

		//- Snowheight
		template(v-if="area.stats.hasSnowheights")
			h2
				nuxt-link.black--text(:to="localePath({name: 'areas-area-snowheight', params: { resort: area.slug }})" :title="$t('page.areas._area.detail:snowheight.heading_link_title_{area}', {area: area.name})") {{$t('page.areas._area.detail:snowheight.heading')}}
			snowheights-stats-card(:snowheight="area.snowheights.nodes[0]")
			v-divider.my-6

		//- Snowheight nearby
		.flow-left
			h2 {{$t('page.areas._area.snowheight:nearby.heading')}}
			snowheights-nearby-table(:coordinates="area.geography.location.coordinates" :selected-area-id="area.id")
			v-divider.my-6

		//- Snowfall
		template(v-if="historyGraphdata")
			h2 {{$t("page.areas._area.snowheight:history.heading")}}
			barchart(:data="historyGraphdata")
			v-divider.my-6

		//- Snowguarantee
		template(v-if="snowguaranteeSki")
			h2 {{$t("page.areas._area:snowguarantee.heading")}}
			ws-content(:markdown="snowguaranteeContentpage.content.introduction" level="3")
			snowguarantee-table.mb-6(:destinations="snowguaranteeSki" skip-name)
			.text-center
				v-btn(color="primary" outlined nuxt :to="localePath({name: 'areas-area-snowguarantee', params: { area: area.slug }})") {{$t('page.areas._area:snowguarantee.more')}}
			v-divider.my-6

		//- Webcams
		template(v-if="area.webcams.length")
			h2
				nuxt-link.black--text(:to="localePath({name: 'areas-area-webcams', params: { area: area.slug }})" :title="$t('page.areas._area.detail:webcams.heading_link_title_{area}', {area: area.name})") {{$t('page.areas._area:webcams.heading')}}
			area-webcam-list(:items="area.webcams")
			.text-center
				v-btn.mt-4(color="primary" outlined nuxt :to="localePath({name: 'areas-area-webcams', params: { area: area.slug }})") {{$t('page.areas._area:webcams.more_{webcams}', {webcams: area.stats.webcamCount})}}
			v-divider.my-6

		//- Restaurants
		template(v-if="area.restaurants.length")
			h2
				nuxt-link.black--text(:to="localePath({name: 'areas-area-restaurants', params: { area: area.slug }})" :title="$t('page.areas._area.detail:restaurants.heading_link_title_{area}', {area: area.name})") {{$t('page.areas._area:restaurants.heading')}}
			ws-content(v-if="restaurantsFeatureText" :markdown="restaurantsFeatureText.text")
			location-list(:items="area.restaurants" limit="6")
			.text-center.mt-6(v-if="area.stats.restaurantCount>6")
				v-btn(:to="localePath({name: 'areas-area-restaurants', params: {area: area.slug}})" color="primary" outlined) {{$t('area-location-list.label.{number}.restaurants-button', {number: area.stats.restaurantCount})}}
			v-divider.my-6

		//- Apres-ski
		template(v-if="area.apresski.length")
			h2
				nuxt-link.black--text(:to="localePath({name: 'areas-area-apresski', params: { area: area.slug }})" :title="$t('page.areas._area.detail:apres-ski.heading_link_title_{area}', {area: area.name})") {{$t('page.areas._area:apres-ski.heading')}}
			ws-content(v-if="apresskiFeatureText" :markdown="apresskiFeatureText.text")
			location-list(:items="area.apresski" limit="6")
			.text-center.mt-6(v-if="area.stats.apresskiCount>6")
				v-btn(:to="localePath({name: 'areas-area-apresski', params: {area: area.slug}})" color="primary" outlined) {{$t('area-location-list.label.{number}.apresski-button', {number: area.stats.apresskiCount})}}
			v-divider.my-6

		//- Similar areas
		template(v-if="area.similars.length >= 2")
			h2 {{$t("page.areas._area:similar.areas.heading")}}
			area-similar(:slugs="[area.slug, area.similars[0].slug, area.similars[1].slug]" compact)
			.text-center
				v-btn.mt-4(color="primary" outlined nuxt :to="localePath({name: 'areas-area-similar', params: {area:area.slug}})") {{$t('page.areas._area:more.similar')}}
			v-divider.my-6

		//- Links
		template(v-if="area.links.length")
			h2 {{$t('page.areas._area:links.heading')}}
			v-row(v-for="link in area.links" :key="link.id" no-gutters)
				v-col.shrink
					ws-icon(small) angle-right
				v-col
					a(:href="link.url" target="_blank" rel="nofollow") {{link.url}}
			v-divider.my-6

		//- podcast
		template(v-if="area.podcast")
			h2 {{area.podcast.title}}
			vue-plyr.my-3(ref="plyr" :options='{"controls":["play", "progress", "volume", "play-large", "airplay", "fullscreen", "pip", "mute"], "disableContextMenu": false}' :key="area.podcast.youtubeId")
				div(data-plyr-provider="youtube" :data-plyr-embed-id="area.podcast.youtubeId")

</template>

<script>
import gql from 'graphql-tag';
import Content from '@dengl/common/components/content.vue';
import GalleryEmbed from '@dengl/embeds/image-gallery/embed.vue';
import TitleWithReviews from '@dengl/common/components/title-with-reviews';
import TopicList from '@dengl/common/components/forum/topic-list.vue';
import WeblogListEmbed from '@dengl/embeds/weblog-list/embed.vue';
import AreaSimilar from '@/components/areas/comparison.vue';
import areasReachability from '@/mixins/areas-reachability.js';
import AreaWebcamList from '@/components/areas/webcam-list.vue';
import Barchart from '@/components/display/barchart.vue';
import CoronaInfo from '@/components/areas/corona-info';
import Factsheet from '@/components/areas/factsheet';
import Features from '@/components/areas/features';
import ForecastTable from '@/components/weather/forecast-table.vue';
import LocationList from '@/components/areas/location-list';
import MetaFragment from '@/components/graphql/metaFragment.graphql';
import ResortTable from '@/components/resort-table.vue';
import ReviewList from '@/components/review-list.vue';
import SkipasPriceTable from '@/components/skipas-price-table.vue';
import SnowguaranteeTable from '@/components/snowguarantee-table.vue';
import snowheightHistoryMixin from '@/mixins/snowheight-history.js';
import SnowheightsNearbyTable from '@/components/snowheights/nearby-table.vue';
import SnowheightsStatsCard from '@/components/snowheights/stats-card.vue';
import Travelbox from '@/components/travelbox.vue';
import UserContentDetails from '@/components/user-content/fragments.js';
import WeatherReport from '@/components/weather-report.vue';

const getAreaDetails = gql`
	query getAreaDetails(
		$slug: String!
		$path: String
		$fallback: String
		$pathSnowguarantee: String
		$fallbackSnowguarantee: String
		$preferedCity: String!
		$pagination: PaginationInput
	) {
		contentpage(path: $path, fallback: $fallback) {
			id
			name
			slug
			content {
				...ContentDataFragment
				markdown3: markdown(level: 3)
			}
			...MetaFragment
		}
		snowguaranteeContentpage: contentpage(
			path: $pathSnowguarantee
			fallback: $fallbackSnowguarantee
		) {
			id
			content {
				markdown
				introduction(length: 300)
			}
		}
		area(slug: $slug) {
			id
			name
			slug
			minElevation
			maxElevation
			country {
				id
				name
				countryCode
				slug
			}
			region {
				id
				name
				slug
			}
			geography {
				location {
					coordinates
				}
			}
			mainResort {
				id
			}
			reviews(limit: 1) {
				nodes {
					...UserContentDetails
				}
				average
				voteCount
				recommended: recommendedPercent
				reviewCount
				voteCount
			}
			defaultVideo {
				videoId
				videoSource
			}
			photos {
				url
				alt
				caption
			}
			pistes {
				content {
					markdown
				}
				name
				color
			}
			features {
				title
				rating
				text
				type
			}
			slopeinfo {
				total
				green_percentage
				blue_percentage
				red_percentage
				black_percentage
				green: slopeGreen
				blue: slopeBlue
				red: slopeRed
				black: slopeBlack
				gondola
				chairlift
				tbar
				magiccarpet
				hasSummerSkiing
				hasConnectedSlopes
			}
			departures {
				highway {
					distance
				}
				city(city: $preferedCity) {
					distance
					node {
						id
						name
						slug
						geography {
							location {
								coordinates
							}
						}
					}
				}
				airports(limit: 1) {
					distance
					node {
						id
						name
					}
				}
			}
			gateways {
				id
				name
				content {
					markdown(raw: true)
				}
			}
			bottlenecks {
				id
				name
				content {
					markdown(raw: true)
				}
			}
			...SkipasInfo
			clusters {
				name
				slug
				totalkms
				...SkipasInfo
			}
			stats {
				webcamCount
				resortCount
				hasForecast
				hasSnowheights
				restaurantCount
				apresskiCount
				rentalCount
				lessonCount
				accommodationCount
				apartmentCount
				hotelCount
				pensionCount
				chaletCount
			}
			resorts {
				nodes {
					id
					name
					slug
					elevation
					hasSkiLift
					hasDownHill
					features {
						title
						rating
						text
						type
					}
				}
			}
			topics(pagination: $pagination) {
				...TopicListFragment
			}
			snowheights {
				maxSnowMountain
				maxSnowValley
				nodes {
					...SnowheightStatCardData
				}
			}
			snowguarantee
			webcams(limit: 6) {
				name
				id
				elevation
				preview
			}
			forecasthistory {
				rundate
				snowTotal
			}
			similars {
				id
				name
				slug
			}
			restaurants: locations(types: [restaurant, pisterestaurant], limit: 6) {
				id
				name
				slug
				photos {
					url
					alt
				}
				resort {
					id
					slug
					name
				}
				reviews {
					voteCount
					average
				}
			}
			apresski: locations(types: [apresski], limit: 6) {
				id
				name
				slug
				photos {
					url
					alt
				}
				resort {
					id
					slug
					name
				}
				reviews {
					voteCount
					average
				}
			}
			links {
				id
				name
				url
			}
			podcast {
				id
				title
				publishDate
				type
				url
				youtubeId
			}
			slopeinfo {
				total
				green_percentage
				blue_percentage
				red_percentage
				black_percentage
				green: slopeGreen
				blue: slopeBlue
				red: slopeRed
				black: slopeBlack
				gondola
				chairlift
				tbar
				magiccarpet
				hasSummerSkiing
				hasConnectedSlopes
			}
			skimap: defaultSkimap {
				url
				alt
				caption
			}
			corona {
				isActive
				measures
				url
			}
		}
		weblogItems: weblogsByCategory(limit: 1, category: "weather") {
			weblogs {
				id
				title
				slug
				content {
					introduction(length: 240)
				}
				publishDate
				url
				photo
				replyCount
				author {
					id
					username
					signature
					teamDescription
					role
				}
			}
		}
	}
	${MetaFragment}
	${UserContentDetails}
	${SkipasPriceTable.fragments}
	${TopicList.fragments}
	${SnowheightsStatsCard.fragments}
	${Content.fragments}
`;

export default {
	nuxtI18n: {
		paths: {
			'nl-NL': '/skigebieden/:area',
			'de-DE': '/skigebiete/:area',
			'nl-BE': '/skigebieden/:area'
		}
	},
	name: 'AreaIndex',
	components: {
		AreaSimilar,
		AreaWebcamList,
		Barchart,
		CoronaInfo,
		Factsheet,
		Features,
		ForecastTable,
		GalleryEmbed,
		LocationList,
		ResortTable,
		ReviewList,
		SkipasPriceTable,
		SnowguaranteeTable,
		SnowheightsNearbyTable,
		SnowheightsStatsCard,
		TitleWithReviews,
		TopicList,
		Travelbox,
		WeatherReport,
		WeblogListEmbed
	},
	mixins: [areasReachability, snowheightHistoryMixin],
	layout: 'default',
	// eslint-disable-next-line @milqmedia/milq/page-has-meta
	asyncData({ params, app, route }) {
		const snowguarantee =
			app.i18n.locale === 'de-DE' ? 'schneesicherheit' : 'sneeuwzekerheid';
		return app.$graphClient
			.query({
				query: getAreaDetails,
				variables: {
					slug: params.area,
					path: route.path,
					fallback: route.path.replace(params.area, '_area'),
					pathSnowguarantee: `${route.path}/${snowguarantee}`,
					fallbackSnowguarantee: `${route.path.replace(
						params.area,
						'_area'
					)}/${snowguarantee}`,
					preferedCity: app.store.getters.preferedCity,
					pagination: { page: 1, limit: 10 }
				},
				errorPolicy: 'all'
			})
			.then(({ data, errors }) => {
				if (!data.contentpage) {
					return app.httpError(404, null, 'areas-area-index-01', errors);
				}
				if (errors) {
					throw errors[0];
				}

				app.interpolater(data.contentpage.meta, ['title', 'description']);
				app.interpolater(data.contentpage.content, ['markdown']);
				app.interpolater(data.contentpage, ['name']);
				if (data.area.skimap) {
					data.contentpage.meta.header = data.area.skimap.url;
					data.contentpage.meta.image = data.area.skimap.url;
				}
				app.meta(data.contentpage.meta);
				app.ads({
					sidebar: {
						visible: true,
						targets: {
							Skiresort: data.area.id,
							State: data.area.region.id,
							Country: data.area.country.id
						}
					}
				});
				app.areaBreadcrumbs(data.area, {});

				return data;
			})
			.catch(app.$destinationNotFoundCatcher)
			.catch(error => {
				return app.httpError(500, null, 'areas-area-index-02', error);
			});
	},
	data() {
		return { showFullIntro: false, loadSimilar: false };
	},
	head() {
		const structuredData = {
			'@context': 'https://schema.org/',
			'@type': 'SkiResort',
			description: this.contentpage.meta.description,
			image: this.contentpage.meta.image,
			name: this.area.name
		};

		if (this.area.reviews.voteCount) {
			structuredData.aggregateRating = {
				'@type': 'AggregateRating',
				ratingValue: this.area.reviews.average,
				ratingCount: this.area.reviews.voteCount,
				reviewCount: this.area.reviews.reviewCount,
				bestRating: 10
			};
		}

		return this.$lodash.merge(this.$structuredData(), {
			script: [
				{
					innerHTML: JSON.stringify(structuredData),
					type: 'application/ld+json',
					hid: 'skiresort-schema'
				}
			]
		});
	},
	computed: {
		introtext() {
			if (!this.contentpage.content) {
				return {
					text: ''
				};
			}
			let introtext = this.contentpage.content.markdown3 || '';
			const introParagraphs = introtext.split('<h3>');
			const isSummary = introParagraphs.length > 3;

			if (!this.showFullIntro) {
				introtext = introParagraphs.slice(0, 3).join('<h3>');
			}

			return {
				...this.contentpage.content,
				markdown: introtext,
				isSummary
			};
		},
		skipasArray() {
			return [
				this.area.skipas,
				...this.area.clusters.map(cluster => {
					return cluster && cluster.skipas
						? { ...cluster.skipas, name: cluster.name }
						: null;
				})
			].filter(v => !!v);
		},
		weblog() {
			return this.weblogItems.weblogs[0];
		},
		snowguaranteeSki() {
			return [
				{
					name: this.area.name,
					snowguarantee: this.area.snowguarantee,
					to: this.localePath({
						name: 'areas-area',
						params: { area: this.area.slug }
					})
				}
			];
		},
		apresskiFeatureText() {
			return this.area.features.find(feature => {
				return feature.type === 'APRESSKI';
			});
		},
		restaurantsFeatureText() {
			return this.area.features.find(feature => {
				return feature.type === 'RESTAURANTS';
			});
		}
	},
	created() {
		this.$emit('update:showSidebar', false);
	}
};
</script>

<style lang="sass">
.area-reviews
	.user-content__row
		border: 0

.cta-video
	position: absolute
	top: 0
	left: 0
	right: 0
	padding: 0 0.8rem
	color: #fff
	background: rgba(22, 25, 39, 0.5)
	z-index: 1

.piste-list
	h3
		font-size: 15px

	@each $name, $color in $piste-colors
		.piste-#{$name}
			.v-icon
				color: $color !important
			h3
				color: $color !important
</style>
